import React from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FilterType, SearchType } from "hooks/search/types";
import { isNonNegativeInteger } from "utils/helpers";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { AgeRangeFilter } from "pages/Books/components/filters/ageRange/AgeRangeFilter";
import { DateFilter } from "pages/Books/components/filters/dateFilter/DateFilter";
import { BisacCodeFilter } from "./BisacCodeFilter";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { NumericComparisonInput } from "./NumericComparisonInput";
import { SalesDataFilter } from "./salesDataFilter/SalesDataFilter";
import { ThemaCodeFilter } from "./ThemaCodeFilter";

interface FilterTypeSelectorProps {
  type: FilterType;
  name: string;
  searchQuery?: string;
  searchType?: SearchType;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
  ageRangeHasOtherFilters?: boolean;
  ageRangeBoundsLoading?: boolean;
}

export const FilterTypeSelector: React.FC<FilterTypeSelectorProps> = ({
  type,
  name,
  searchQuery,
  searchType,
  ageRangeAvailableBounds,
  ageRangeHasOtherFilters,
  ageRangeBoundsLoading,
}) => {
  const { t } = useTranslation(["books"]);
  const numericValue = useWatch({ name: `filters.${name}.value` });

  switch (type) {
    case FilterType.NUMERIC:
      return (
        <div className="px-6">
          <NumericComparisonInput
            valueName={`filters.${name}.value`}
            comparisonName={`filters.${name}.comparisonOperator`}
            ariaLabel={`${name} input`}
            error={
              numericValue !== undefined && !isNonNegativeInteger(numericValue)
                ? t("books:filters.errors.invalidCount")
                : undefined
            }
          />
        </div>
      );
    case FilterType.DATE_RANGE:
      return (
        <div className="px-6">
          <DateFilter name={name} />
        </div>
      );
    case FilterType.AGE_RANGE:
      return (
        <div className="px-6">
          <AgeRangeFilter
            name={name}
            availableBounds={ageRangeAvailableBounds}
            hasOtherActiveFilters={ageRangeHasOtherFilters}
            boundsLoading={ageRangeBoundsLoading}
          />
        </div>
      );
    case FilterType.SALES_DATA:
      return <SalesDataFilter />;
    case FilterType.CHECKBOX:
      if (name === "bisacCodes") {
        return (
          <BisacCodeFilter
            name={name}
            searchQuery={searchQuery}
            searchType={searchType}
          />
        );
      }

      if (name === "themaCodes") {
        return (
          <ThemaCodeFilter
            name={name}
            searchQuery={searchQuery}
            searchType={searchType}
          />
        );
      }

      return (
        <MultiSelectFilter
          name={name}
          searchQuery={searchQuery}
          searchType={searchType}
        />
      );
    default:
      return null;
  }
};
